import React, { useEffect } from "react";

import { LabeledControl } from "@replo/design-system/blocks/labeled-control/LabeledControl";
import PageHeader from "@replo/design-system/blocks/page-header/PageHeader";
import { Avatar } from "@replo/design-system/components/avatar/Avatar";
import { Button } from "@replo/design-system/components/button/Button";
import { Input } from "@replo/design-system/components/input/Input";
import { Separator } from "@replo/design-system/components/separator/Separator";
import { Tag } from "@replo/design-system/components/tag/Tag";
import { toast } from "@replo/design-system/components/toast/Toast";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import GoogleAuth from "@v2/features/account/GoogleAuth";
import { useProfileForm } from "@v2/features/settings/hooks/useProfileForm";
import {
  LoadedSettings,
  SettingsLayout,
} from "@v2/features/settings/SettingsCard";
import { CheckCircle2 } from "lucide-react";

const Profile = () => {
  const {
    user,
    registerName,
    handleNameSubmit,
    nameErrors,
    isUpdatingName,
    hasNameChanged,
    registerPassword,
    handlePasswordSubmit,
    passwordErrors,
    isUpdatingPassword,
    canUpdatePassword,
    googleErrorMessage,
    handleGoogleConnect,
  } = useProfileForm();

  useEffect(() => {
    if (googleErrorMessage) {
      toast.error({
        title: "Verification Error",
        description: googleErrorMessage,
      });
    }
  }, [googleErrorMessage]);

  const sections = [
    {
      component: (
        <div className="flex flex-col gap-4 w-full">
          <div className="flex items-center gap-2">
            <Avatar
              size="lg"
              name={user?.name}
              imageClassName="bg-sky-soft text-primary text-2xl font-semibold"
            />
            <div className="flex flex-col">
              <div className="text-sm font-medium">{user?.name}</div>
              <span className="text-sm text-placeholder">{user?.email}</span>
            </div>
          </div>
          <Separator />
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => void handleNameSubmit(e)}
          >
            <div className="flex w-full gap-2">
              <LabeledControl
                label="First Name"
                className="flex-1"
                error={nameErrors.firstName?.message}
              >
                <Input
                  placeholder="Enter your first name"
                  size="base"
                  {...registerName("firstName")}
                />
              </LabeledControl>
              <LabeledControl
                label="Last Name"
                className="flex-1"
                error={nameErrors.lastName?.message}
              >
                <Input
                  placeholder="Enter your last name"
                  size="base"
                  {...registerName("lastName")}
                />
              </LabeledControl>
            </div>
            <div className="flex flex-col gap-2">
              <LabeledControl
                label="Email"
                description="To change your email, please reach out to support@replo.app."
                className="flex-1"
              >
                <Input
                  placeholder="Enter your email"
                  size="base"
                  disabled
                  value={user?.email ?? ""}
                  endEnhancer={
                    <>
                      {user?.verifiedAt && (
                        <Tooltip content="Email verified" triggerAsChild>
                          <Tag variant="success" size="sm">
                            <CheckCircle2 className="text-emerald" />
                            Verified
                          </Tag>
                        </Tooltip>
                      )}
                    </>
                  }
                />
              </LabeledControl>
              <div className="flex justify-start">
                {!user?.verifiedAt && (
                  <GoogleAuth
                    handleGoogleCredentials={handleGoogleConnect}
                    className="justify-start max-w-[230px]"
                  />
                )}
              </div>
            </div>
            <div className="flex w-full justify-end">
              <Button
                variant="primary"
                size="base"
                type="submit"
                loading={isUpdatingName}
                disabled={isUpdatingName || !hasNameChanged}
              >
                Update Profile
              </Button>
            </div>
          </form>
        </div>
      ),
    },
    {
      title: "Update Password",
      component: (
        <form
          className="flex flex-col gap-4 w-full"
          onSubmit={(e) => void handlePasswordSubmit(e)}
        >
          <div className="flex w-full gap-2">
            <LabeledControl
              label="Current Password"
              className="flex-1"
              error={passwordErrors.password?.message}
            >
              <Input
                placeholder="••••••••••••"
                size="base"
                type="password"
                invalid={Boolean(passwordErrors.password?.message)}
                {...registerPassword("password")}
              />
            </LabeledControl>
            <LabeledControl
              label="New Password"
              className="flex-1"
              error={passwordErrors.newPassword?.message}
            >
              <Input
                placeholder="••••••••••••"
                size="base"
                type="password"
                invalid={Boolean(passwordErrors.newPassword?.message)}
                {...registerPassword("newPassword")}
              />
            </LabeledControl>
          </div>
          <div className="flex w-full justify-end">
            <Button
              variant="primary"
              size="base"
              type="submit"
              loading={isUpdatingPassword}
              disabled={!canUpdatePassword}
            >
              Update Password
            </Button>
          </div>
        </form>
      ),
    },
  ];

  return (
    <div className="flex flex-col max-w-[800px] w-full gap-4">
      <PageHeader title="Account Settings" />
      <SettingsLayout maxWidth="max-w-[800px]">
        <LoadedSettings sections={sections} />
      </SettingsLayout>
    </div>
  );
};

export default Profile;
